/** Seven-locale copy for equipment dates, derived usage counts and the
 * Recovery & Equipment Stats section. */
type Pack = {
  equipment: Record<string, string>;
  equipmentStats: Record<string, string>;
};

const b = (d: string) => ({
  bucket_0_1: `0–1 ${d}`, bucket_2_3: `2–3 ${d}`, bucket_4_7: `4–7 ${d}`, bucket_8_14: `8–14 ${d}`, bucket_15_plus: `15+ ${d}`,
  age_0_7: `≤7 ${d}`, age_8_14: `8–14 ${d}`, age_15_30: `15–30 ${d}`, age_31_60: `31–60 ${d}`, age_61_90: `61–90 ${d}`, age_91_plus: `91+ ${d}`,
});

export const EQUIPMENT_STATS_OVERLAY: Record<string, Pack> = {
  en: {
    equipment: {
      title: "Equipment",
      shoesLabel: "Shoes purchased / started using",
      racketUses_one: "{{count}} recorded match since stringing",
      racketUses_other: "{{count}} recorded matches since stringing",
      shoesUses_one: "{{count}} recorded match with this pair",
      shoesUses_other: "{{count}} recorded matches with this pair",
    },
    equipmentStats: {
      sectionTitle: "Recovery & equipment",
      recoveryTitle: "Win rate by days since previous match",
      recoveryHint: "Gap to your actual previous recorded match. n = decided matches per bar.",
      recoveryEmpty: "Log at least two matches to see how recovery time affects results.",
      racketTitle: "Win rate by string age",
      racketHint: "Days between stringing and the match. n = decided matches per bar.",
      racketEmpty: "Record a racket stringing date on your matches to unlock this chart.",
      shoesTitle: "Win rate by shoe age",
      shoesHint: "Days since you started using the pair. n = decided matches per bar.",
      shoesEmpty: "Record when you started using your shoes to unlock this chart.",
      ...b("days"),
    },
  },
  el: {
    equipment: {
      title: "Εξοπλισμός",
      shoesLabel: "Αγορά / έναρξη χρήσης παπουτσιών",
      racketUses_one: "{{count}} καταγεγραμμένος αγώνας από το πλέξιμο",
      racketUses_other: "{{count}} καταγεγραμμένοι αγώνες από το πλέξιμο",
      shoesUses_one: "{{count}} καταγεγραμμένος αγώνας με αυτό το ζευγάρι",
      shoesUses_other: "{{count}} καταγεγραμμένοι αγώνες με αυτό το ζευγάρι",
    },
    equipmentStats: {
      sectionTitle: "Αποκατάσταση & εξοπλισμός",
      recoveryTitle: "Ποσοστό νικών ανά ημέρες από τον προηγούμενο αγώνα",
      recoveryHint: "Απόσταση από τον πραγματικό προηγούμενο καταγεγραμμένο αγώνα. n = αγώνες με αποτέλεσμα ανά στήλη.",
      recoveryEmpty: "Κατάγραψε τουλάχιστον δύο αγώνες για να δεις πώς επηρεάζει η ξεκούραση τα αποτελέσματα.",
      racketTitle: "Ποσοστό νικών ανά ηλικία πλεξίματος",
      racketHint: "Ημέρες από το πλέξιμο μέχρι τον αγώνα. n = αγώνες με αποτέλεσμα ανά στήλη.",
      racketEmpty: "Κατάγραψε ημερομηνία πλεξίματος ρακέτας για να ενεργοποιηθεί αυτό το γράφημα.",
      shoesTitle: "Ποσοστό νικών ανά ηλικία παπουτσιών",
      shoesHint: "Ημέρες από την έναρξη χρήσης του ζευγαριού. n = αγώνες με αποτέλεσμα ανά στήλη.",
      shoesEmpty: "Κατάγραψε πότε άρχισες να φοράς τα παπούτσια σου για να ενεργοποιηθεί αυτό το γράφημα.",
      ...b("ημ."),
    },
  },
  es: {
    equipment: {
      title: "Equipamiento",
      shoesLabel: "Zapatillas compradas / en uso desde",
      racketUses_one: "{{count}} partido registrado desde el encordado",
      racketUses_other: "{{count}} partidos registrados desde el encordado",
      shoesUses_one: "{{count}} partido registrado con este par",
      shoesUses_other: "{{count}} partidos registrados con este par",
    },
    equipmentStats: {
      sectionTitle: "Recuperación y equipamiento",
      recoveryTitle: "Victorias según días desde el partido anterior",
      recoveryHint: "Distancia a tu partido anterior real registrado. n = partidos decididos por barra.",
      recoveryEmpty: "Registra al menos dos partidos para ver cómo influye la recuperación.",
      racketTitle: "Victorias según antigüedad del encordado",
      racketHint: "Días entre el encordado y el partido. n = partidos decididos por barra.",
      racketEmpty: "Registra la fecha de encordado para desbloquear este gráfico.",
      shoesTitle: "Victorias según antigüedad de las zapatillas",
      shoesHint: "Días desde que empezaste a usar el par. n = partidos decididos por barra.",
      shoesEmpty: "Registra cuándo empezaste a usar tus zapatillas para desbloquear este gráfico.",
      ...b("días"),
    },
  },
  fr: {
    equipment: {
      title: "Équipement",
      shoesLabel: "Chaussures achetées / utilisées depuis",
      racketUses_one: "{{count}} match enregistré depuis le cordage",
      racketUses_other: "{{count}} matchs enregistrés depuis le cordage",
      shoesUses_one: "{{count}} match enregistré avec cette paire",
      shoesUses_other: "{{count}} matchs enregistrés avec cette paire",
    },
    equipmentStats: {
      sectionTitle: "Récupération et équipement",
      recoveryTitle: "Taux de victoire selon les jours depuis le match précédent",
      recoveryHint: "Écart avec votre vrai match précédent enregistré. n = matchs décidés par barre.",
      recoveryEmpty: "Enregistrez au moins deux matchs pour voir l’effet de la récupération.",
      racketTitle: "Taux de victoire selon l’âge du cordage",
      racketHint: "Jours entre le cordage et le match. n = matchs décidés par barre.",
      racketEmpty: "Enregistrez une date de cordage pour débloquer ce graphique.",
      shoesTitle: "Taux de victoire selon l’âge des chaussures",
      shoesHint: "Jours depuis le début d’utilisation de la paire. n = matchs décidés par barre.",
      shoesEmpty: "Enregistrez quand vous avez commencé vos chaussures pour débloquer ce graphique.",
      ...b("j"),
    },
  },
  de: {
    equipment: {
      title: "Ausrüstung",
      shoesLabel: "Schuhe gekauft / in Gebrauch seit",
      racketUses_one: "{{count}} erfasstes Match seit der Bespannung",
      racketUses_other: "{{count}} erfasste Matches seit der Bespannung",
      shoesUses_one: "{{count}} erfasstes Match mit diesem Paar",
      shoesUses_other: "{{count}} erfasste Matches mit diesem Paar",
    },
    equipmentStats: {
      sectionTitle: "Erholung & Ausrüstung",
      recoveryTitle: "Siegquote nach Tagen seit dem letzten Match",
      recoveryHint: "Abstand zum tatsächlich vorherigen erfassten Match. n = entschiedene Matches pro Balken.",
      recoveryEmpty: "Erfasse mindestens zwei Matches, um den Einfluss der Erholung zu sehen.",
      racketTitle: "Siegquote nach Alter der Bespannung",
      racketHint: "Tage zwischen Bespannung und Match. n = entschiedene Matches pro Balken.",
      racketEmpty: "Erfasse ein Bespannungsdatum, um dieses Diagramm freizuschalten.",
      shoesTitle: "Siegquote nach Alter der Schuhe",
      shoesHint: "Tage seit Nutzungsbeginn des Paars. n = entschiedene Matches pro Balken.",
      shoesEmpty: "Erfasse, seit wann du deine Schuhe trägst, um dieses Diagramm freizuschalten.",
      ...b("Tage"),
    },
  },
  it: {
    equipment: {
      title: "Attrezzatura",
      shoesLabel: "Scarpe acquistate / in uso dal",
      racketUses_one: "{{count}} partita registrata dall’incordatura",
      racketUses_other: "{{count}} partite registrate dall’incordatura",
      shoesUses_one: "{{count}} partita registrata con questo paio",
      shoesUses_other: "{{count}} partite registrate con questo paio",
    },
    equipmentStats: {
      sectionTitle: "Recupero e attrezzatura",
      recoveryTitle: "Vittorie per giorni dalla partita precedente",
      recoveryHint: "Distanza dalla reale partita precedente registrata. n = partite decise per barra.",
      recoveryEmpty: "Registra almeno due partite per vedere l’effetto del recupero.",
      racketTitle: "Vittorie per età dell’incordatura",
      racketHint: "Giorni tra incordatura e partita. n = partite decise per barra.",
      racketEmpty: "Registra una data di incordatura per sbloccare questo grafico.",
      shoesTitle: "Vittorie per età delle scarpe",
      shoesHint: "Giorni da quando usi il paio. n = partite decise per barra.",
      shoesEmpty: "Registra quando hai iniziato a usare le scarpe per sbloccare questo grafico.",
      ...b("gg"),
    },
  },
  ja: {
    equipment: {
      title: "用具",
      shoesLabel: "シューズ購入日／使用開始日",
      racketUses_other: "張り替え後の記録試合数：{{count}}",
      shoesUses_other: "このシューズでの記録試合数：{{count}}",
    },
    equipmentStats: {
      sectionTitle: "回復と用具",
      recoveryTitle: "前の試合からの日数別勝率",
      recoveryHint: "実際に記録された直前の試合からの日数。n＝各棒の勝敗が決した試合数。",
      recoveryEmpty: "回復期間の影響を見るには2試合以上記録してください。",
      racketTitle: "ガットの経過日数別勝率",
      racketHint: "張り替えから試合までの日数。n＝各棒の勝敗が決した試合数。",
      racketEmpty: "ガットの張り替え日を記録するとこのグラフが表示されます。",
      shoesTitle: "シューズの経過日数別勝率",
      shoesHint: "シューズの使用開始からの日数。n＝各棒の勝敗が決した試合数。",
      shoesEmpty: "シューズの使用開始日を記録するとこのグラフが表示されます。",
      ...b("日"),
    },
  },
};
